import { lazy, Suspense, useEffect, useRef } from "react";
import { canAccessAdmin, isRespondentOnly } from "@/domain/permissions";
import { ALL_LOCATIONS_ID } from "@/domain/systemAnalysis";
import { ListTab } from "@/features/list/ListTab";
import { MatrixTab } from "@/features/matrix/MatrixTab";
import { SystemTab } from "@/features/system/SystemTab";
import { Toolbar } from "@/features/toolbar/Toolbar";
import { useI18n } from "@/i18n/useI18n";
import { useApp, useUi, useUiDispatch } from "./AppContext";
import { AppHeader } from "./AppHeader";
import { PageTitle } from "./PageTitle";
import { isEditorPanel } from "./uiState";
import { readUrlState, type Tab } from "./urlState";

// Chart.js and the admin screens are only loaded when their tab is opened.
const StatsTab = lazy(() =>
  import("@/features/stats/StatsTab").then((module) => ({ default: module.StatsTab })),
);
const AdminTab = lazy(() =>
  import("@/features/admin/AdminTab").then((module) => ({ default: module.AdminTab })),
);

/** Tabs available to the signed-in user, in display order. */
function useAvailableTabs(): Tab[] {
  const { authMode, user } = useApp();
  if (authMode === "individual" && isRespondentOnly(user)) return ["list"];
  const tabs: Tab[] = ["list", "matrix", "stats", "system"];
  if (authMode === "individual" && canAccessAdmin(user)) tabs.push("admin");
  return tabs;
}

const TAB_LABEL_KEYS = {
  list: "tabList",
  matrix: "tabMatrix",
  stats: "tabStats",
  system: "tabSystem",
  admin: "tabAdmin",
} as const;

/**
 * Applies the `?location=` parameter once, after the data is loaded. Without
 * one, a single available location is preselected in the location filter and
 * in the system analysis; users can still switch to all locations.
 */
function useInitialLocationFilter() {
  const { data } = useApp();
  const dispatch = useUiDispatch();
  const applied = useRef(false);
  useEffect(() => {
    if (applied.current || data.status.state !== "ready") return;
    applied.current = true;
    const requested = readUrlState().locations;
    const regions = data.dataset.regions.filter(Boolean);
    if (requested.length) {
      const matches = regions.filter((region) => requested.includes(region.toLowerCase()));
      if (matches.length) dispatch({ type: "setRegions", regions: new Set(matches) });
      return;
    }
    if (regions.length === 1) dispatch({ type: "setRegions", regions: new Set(regions) });
    const systemLocations = new Set(
      data.systemData.analyses
        .flatMap((analysis) => analysis.locations.map((location) => location.locationId))
        .filter((locationId) => locationId !== ALL_LOCATIONS_ID),
    );
    if (systemLocations.size === 1)
      dispatch({ type: "selectSystemLocation", locationId: [...systemLocations][0]! });
  }, [data.status.state, data.dataset.regions, data.systemData, dispatch]);
}

export function Shell() {
  const { t } = useI18n();
  const { authMode, user, data } = useApp();
  const ui = useUi();
  const dispatch = useUiDispatch();
  const tabs = useAvailableTabs();
  useInitialLocationFilter();

  const isRespondentUi = authMode === "individual" && isRespondentOnly(user);
  // Respondents land on the list; the matrix tab is only used for their editor.
  const activeTab =
    tabs.includes(ui.tab) || (isRespondentUi && ui.tab === "matrix" && isEditorPanel(ui.panel))
      ? ui.tab
      : (tabs[0] ?? "matrix");

  return (
    <div id="app-root">
      <AppHeader />
      <PageTitle tab={activeTab} />
      {data.status.state === "error" && (
        <p className="database-load-error" role="alert">
          {`${t("databaseLoadError")} ${data.status.error.message}`}
        </p>
      )}
      {!isRespondentUi && (
        <div className="tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              className={`tab${activeTab === tab ? " active" : ""}`}
              aria-selected={activeTab === tab}
              onClick={() => dispatch({ type: "setTab", tab })}
            >
              {t(TAB_LABEL_KEYS[tab])}
            </button>
          ))}
        </div>
      )}
      <Toolbar tab={activeTab} />
      {activeTab === "matrix" && <MatrixTab />}
      {activeTab === "list" && <ListTab />}
      {activeTab === "system" && <SystemTab />}
      <Suspense fallback={<div aria-busy="true">…</div>}>
        {activeTab === "stats" && <StatsTab />}
        {activeTab === "admin" && <AdminTab />}
      </Suspense>
    </div>
  );
}
